import {
  HEIC_TRANSCODE_JPEG_QUALITY,
  HEIC_TRANSCODE_TIMEOUT_MS,
} from './MediaCompression.constants';
import { replaceFileExtension } from './MediaCompression.functions';
import {
  eCompressionFailReason,
  tNormalizeImageResult,
} from './MediaCompression.types';

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Count of decodes that blew the timeout and are still running.
 *
 * heic-to lazily creates ONE Worker for the whole page, reuses it for every
 * decode, and exposes no `terminate()` / abort API. Its worker handler calls
 * libheif's decode synchronously, so a decode that never settles does not
 * quietly finish in the background — it holds the worker's only JS thread and
 * every later decode queues behind it forever. Without this counter each
 * subsequent HEIC send would wait out its own full timeout and then wrongly
 * blame the user's file.
 *
 * Self-healing: a merely slow decode (as opposed to a wedged one) eventually
 * settles and decrements, and HEIC sends resume without a reload.
 */
let abandonedDecodes = 0;

function trackAbandonedDecode(decode: Promise<unknown>): void {
  abandonedDecodes += 1;
  const release = () => {
    abandonedDecodes -= 1;
  };
  // Attaching both handlers also consumes the orphan's eventual rejection, so a
  // post-timeout failure never surfaces as an unhandled rejection.
  void decode.then(release, release);
}

/**
 * Races the decode against the timeout. Whichever settles first wins and the
 * timer is cleared on both paths. Only a timeout can leave a decode still
 * running inside the shared worker, so only that path registers an orphan.
 */
async function decodeWithTimeout(
  heicTo: typeof import('heic-to').heicTo,
  file: File,
): Promise<Blob> {
  const decode = heicTo({
    blob: file,
    type: 'image/jpeg',
    quality: HEIC_TRANSCODE_JPEG_QUALITY,
  });

  let timer: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;

  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      timedOut = true;
      reject(
        new Error(`HEIC decode timed out after ${HEIC_TRANSCODE_TIMEOUT_MS}ms`),
      );
    }, HEIC_TRANSCODE_TIMEOUT_MS);
  });

  try {
    return await Promise.race([decode, timeout]);
  } catch (error) {
    if (timedOut) {
      trackAbandonedDecode(decode);
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Decodes a HEIC/HEIF file and re-encodes it as JPEG.
 *
 * heic-to (libheif compiled to WebAssembly) is the only browser-side HEIC
 * decoder — Chrome/Firefox/Edge cannot decode HEIC natively. It is imported
 * dynamically so its self-contained wasm bundle stays in its own lazy chunk and
 * never reaches the main bundle (mirrors the mediabunny video path). Never add a
 * static `import … from 'heic-to'` anywhere or that guarantee breaks.
 *
 * ⚠️ CSP: the default build instantiates libheif via `new Function`, so it needs
 * `script-src 'unsafe-eval'`. That grant comes from the **CloudFront Response
 * Headers Policy** attached to the SPA distribution (edited by hand in the AWS
 * console) — NOT from the nginx-api-gateway configmap, which does not govern
 * this page's CSP at all. To see what is actually served, run
 * `curl -sI https://crm.leadsales.services/` and confirm `script-src` still
 * contains `'unsafe-eval'` and `blob:` (there is no `worker-src`/`child-src`, so
 * `worker-src` falls back to `script-src`). The default build is chosen over
 * `heic-to/csp` because that build is ~3× slower per decode (measured ~22s vs
 * ~7.5s on a 12MP photo) and this is a hot user-facing send path. If
 * `'unsafe-eval'` is ever dropped from that CloudFront policy, switch the import
 * below to `'heic-to/csp'` — the failure is not silent (heicTo throws →
 * PROCESSING_ERROR → an error toast, and ImageContent's honest-copy fallback
 * still fixes rendering), but every HEIC send would fail.
 *
 * The failure modes stay distinct: a decoder-chunk load failure (deploy skew /
 * offline) or a poisoned shared worker is ENCODER_UNAVAILABLE ("reload"), while
 * a decode failure or timeout on one file is PROCESSING_ERROR ("convert to
 * JPG"). Conflating them would tell a user to convert a perfectly good file when
 * the decoder simply was not usable.
 */
async function transcodeHeicToJpeg(
  file: File,
): Promise<tNormalizeImageResult> {
  // An earlier decode timed out and still owns heic-to's single shared worker.
  // Queueing behind it would burn another full timeout and then wrongly blame
  // this file, so fail immediately with the guidance that actually works: only a
  // reload can rebuild the worker.
  if (abandonedDecodes > 0) {
    return {
      ok: false,
      reason: eCompressionFailReason.ENCODER_UNAVAILABLE,
      cause: `${abandonedDecodes} HEIC decode(s) still hung; heic-to's shared worker cannot be recovered without a page reload`,
    };
  }

  let heicTo: typeof import('heic-to').heicTo;
  try {
    ({ heicTo } = await import('heic-to'));
  } catch (error) {
    return {
      ok: false,
      reason: eCompressionFailReason.ENCODER_UNAVAILABLE,
      cause: errorMessage(error),
    };
  }

  try {
    const jpegBlob = await decodeWithTimeout(heicTo, file);

    const jpegFile = new File(
      [jpegBlob],
      replaceFileExtension(file.name, '.jpg'),
      { type: 'image/jpeg' },
    );

    return { ok: true, file: jpegFile, wasTranscoded: true };
  } catch (error) {
    return {
      ok: false,
      reason: eCompressionFailReason.PROCESSING_ERROR,
      cause: errorMessage(error),
    };
  }
}

export const HeicTranscodeService = Object.freeze({ transcodeHeicToJpeg });
